import { API_BASE_URL, ApiError } from './client';
import type { PhotoUploadAudience } from '../lib/photoAudience';

/**
 * The event docket — TROOP-116.
 *
 * One read that answers every closed row on the event page. The page used to mount five sections
 * that each fetched on sight, so an officer opening an ordinary campout paid seventeen requests in
 * three waves before anybody had asked to see anything.
 *
 * **A null section means the row does not exist for this caller** — not that it is empty. The
 * server decides that once, so four components no longer each mount and decide for themselves.
 */
export interface EventDocket {
  going: DocketGoing | null;
  forms: DocketForms | null;
  bring: DocketBring | null;
  travel: DocketTravel | null;
  photos: DocketPhotos | null;
  program: DocketProgram | null;
}

/**
 * `noResponse` is its own number and is never folded into `notGoing` — silence is not a decline.
 *
 * `mineAnswered` / `mineTotal` are how the row knows it is waiting on somebody: the people this
 * caller answers for, and how many of them have said anything.
 */
export interface DocketGoing {
  going: number;
  notGoing: number;
  maybe: number;
  noResponse: number;
  expected: number;
  mineAnswered: number;
  mineTotal: number;
  canAnswerForOthers: boolean;
}

/**
 * What the event asks, counted for this caller's own family.
 *
 * **There is no leader roll-up here on purpose.** "9 of 14 returned" cannot be written without
 * adding a scan to a tick, and §7 keeps those apart — a scan is evidence, a tick is a claim. The
 * officer's two numbers stay in the panel, where they are already rendered as two.
 */
export interface DocketForms {
  asked: number;
  mineOutstanding: number;
  canManage: boolean;
}

/**
 * What to bring — this caller's own, and nobody else's, whoever is asking.
 *
 * **No leader roll-up, on purpose.** A packing list is opt-in, so "0 of 2 ready" divides the few
 * who chose to use it by everybody, and the gap measures adoption rather than readiness. The
 * packing panel still offers an officer the count when they open it.
 */
export interface DocketBring {
  mine: DocketBringList[];
  /**
   * How many lists the event carries at all. It separates the two ways of having nothing to bring:
   * zero means nothing is being asked of anybody, non-zero with an empty `mine` means there is a
   * list here but nothing on it is yours yet.
   */
  lists: number;
  /**
   * Whether to offer the panel's Manage control. **Never a substitute for a list's own
   * `canWrite`**, which is asked per row — a parent writes their child's list while only reading
   * the unit's, on one screen.
   */
  canManage: boolean;
}

export interface DocketBringList {
  personId: string;
  displayName: string;
  checked: number;
  total: number;
}

/**
 * Getting there and back.
 *
 * **There is no `seatsNeeded`, deliberately.** It used to be here beside `seatsOffered` and the row
 * subtracted the two, which nets a spare seat on one leg against a missing one on another: twelve
 * seats on the outbound and none on the return read "12 seats · full" while nobody was driving
 * anybody home. Coverage is decided per leg, so the server sends how many legs are short and how
 * many seats are missing across them. Do not add a total of riders back.
 *
 * `unanswered` is zero for anybody who is not an adult officer, and is the largest leg's count
 * rather than the sum — it is the same number on every leg of one event.
 */
export interface DocketTravel {
  legs: number;
  /** Only ever asked whether it is zero, which separates "nobody driving yet" from "somebody is". */
  seatsOffered: number;
  /** Legs with fewer seats than riders. Zero is covered. */
  legsShort: number;
  /** Shortfalls added together, surpluses excluded rather than offset. */
  seatsShort: number;
  unanswered: number;
  canManage: boolean;
  /**
   * Present only when the plan reads as an ordinary there-and-back, which the server derives on
   * every read from the legs themselves. **Two booleans and no number**: the row names the
   * direction that is short, because any figure spanning both is one a Friday surplus can flatter.
   */
  roundTrip: { thereIsShort: boolean; homeIsShort: boolean } | null;
}

/**
 * What is actually happening at this event.
 *
 * **The row carries the theme, not a count.** Every other row states a number because there
 * is one worth stating; the program's honest answer at a glance is the line saying what this
 * one is about. Inventing "7 of 9 duties assigned" to fill the shape would be a metric nobody
 * asked for.
 *
 * The plan itself is not here -- it travels on the event, which the page has already loaded,
 * so the panel opens without a second request.
 */
export interface DocketProgram {
  theme: string | null;
  /**
   * Duties this reader answers for that are not ticked yet, and how many they answer for at
   * all -- theirs, their children's, and their groups'.
   *
   * **The only genuinely per-caller number on this row**, which is why it is the one that
   * gets counted. A leader roll-up here would repeat the mistake the Bring row already
   * refuses: it would measure who has bothered to tick rather than who is ready.
   */
  yoursOutstanding: number;
  yoursTotal: number;
  /** Whether anything has been written at all, which is a different state from no theme. */
  hasPlan: boolean;
  /**
   * Whether this reader may write it. **Position only** -- a youth Patrol Leader is exactly
   * who this is true for, because the SPL plans the meeting. Asked per response rather than
   * hardcoded, for the reason a per-row answer always is: a frozen constant fails silent
   * rather than closed (TROOP-73).
   */
  canWrite: boolean;
}

/** `enabled` is the unit's switch. False closes the gallery rather than emptying it. */
export interface DocketPhotos {
  count: number;
  enabled: boolean;
  /**
   * Whether this reader may add — the unit's choice of audience, judged on the server (TROOP-225).
   * Asked here and not derived from a permission level: a guardian holds none, and the answer turns
   * on their classification as well as their position.
   */
  canAdd: boolean;
  /**
   * Who the unit does let add, so the panel can name them rather than silently omitting a control.
   * **Meaningless while `enabled` is false** — a unit that has turned photos off has said nothing
   * about who would add them — so do not render it then.
   */
  uploadAudience: PhotoUploadAudience;
}

/**
 * `unitId` absent picks the guardian's door — the same two-door switch the photo and transport
 * clients use. Never a second set of rules, only a second path.
 */
const base = (unitId: string | undefined, eventId: string) =>
  unitId
    ? `${API_BASE_URL}/units/${unitId}/events/${eventId}/docket`
    : `${API_BASE_URL}/family/events/${eventId}/docket`;

/** Null when there is no such event, or none this caller may read. */
export async function loadEventDocket(
  unitId: string | undefined,
  eventId: string,
): Promise<EventDocket | null> {
  const response = await fetch(base(unitId, eventId), { credentials: 'include' });

  if (response.status === 404) return null;

  if (!response.ok) {
    throw new ApiError(response.status, `GET docket for ${eventId} failed`);
  }

  return (await response.json()) as EventDocket;
}
